// @ts-check
import {
  lightColorPalette,
  DropDown,
  MenuItem,
  TimeZoneDropdown,
  TimeDropdown
} from '@linearb/linui-common';
import { Stack } from '@mui/material';
import { Body1 } from '@linearb/linui-common/typography';
import { useWizard } from 'react-use-wizard';
import { WORK_DAYS, TRACKING } from 'common/constants/constants';
import { useState } from 'react';
import { EditYourUserProfile } from './EditYourUserProfile';
import useUserProfile from 'views/root/views/home/hooks/useUserProfile';
import useWorkHours from './useWorkHours';
import { convertTo24Hour } from 'common/utils/time-utils';
import * as TrackingService from 'common/services/tracking.service';
import { STEPS } from './steps.consts';
import { LBButton } from '@linearb/buzz';

/**
 * AddWorkHoursStep component renders the final ste  of the user profile wizard, allowing the user to configure notification preferences.
 * @param {object} props
 * @param {Function} props.setOpenWizard - A function to toggle the wizard's open state.
 * @param {Function} props.setOpenCompleteSnackBar - A function to toggle the complete snackbar.
 * @returns {JSX.Element} The fourth step of the user profile wizard.
 */
function AddWorkHoursStep({ setOpenWizard, setOpenCompleteSnackBar }) {
  const { previousStep } = useWizard();
  const { onSave, accountId, orgId } = useUserProfile();
  const {
    timeInitialState,
    timeZoneInitialState,
    workingDaysInitialState,
    workingDaysByDisplayValue
  } = useWorkHours();

  const [workingDays, setWorkingDays] = useState(workingDaysInitialState);
  const [selectedTime, setSelectedTime] = useState(timeInitialState);
  const [timeZone, setTimeZone] = useState(timeZoneInitialState);

  const handleWorkingDaysChange = event => {
    setWorkingDays(event.target.value);
  };

  const handleCompleteProfile = () => {
    const time = convertTo24Hour(selectedTime);
    const workingDaysValue =
      workingDaysByDisplayValue[workingDays]?.value ||
      workingDaysByDisplayValue[WORK_DAYS[0]].value;

    onSave({
      workingDays: workingDaysValue,
      time,
      timeZone,
      currentStep: undefined,
      doNotShowAgain: true
    });

    TrackingService.logEvent(TRACKING.EVENTS.USER_PROFILE.EVENT, {
      event_type: TRACKING.EVENTS.USER_PROFILE.EVENT_TYPE.ADD_WORK_HOURS,
      user_id: accountId,
      org_id: orgId,
      working_days: workingDays,
      preferred_time: selectedTime,
      time_zone: timeZone
    });

    setOpenCompleteSnackBar(true);
    setOpenWizard(false);
  };

  const handleOnBack = () => {
    TrackingService.logEvent(TRACKING.EVENTS.USER_PROFILE.EVENT, {
      event_type: TRACKING.EVENTS.USER_PROFILE.EVENT_TYPE.BACK,
      user_id: accountId,
      org_id: orgId,
      back_from: STEPS.ADD_WORK_HOURS,
      back_to: STEPS.ADD_INTERESTS
    });
    previousStep();
  };

  return (
    <>
      <Stack direction='column'>
        <Body1 color={lightColorPalette.lbTextSubdued}>
          Last but not least - choose when will it be best to notify you, so our
          insights will always get to you on time when you need them.
        </Body1>
        <EditYourUserProfile />
        <Body1
          color={lightColorPalette.lbTextSubdued}
          paddingTop={2}
          paddingBottom={1}
        >
          Define your <strong>working days</strong>:
        </Body1>
        <DropDown
          sx={{ color: `${lightColorPalette.lbText} !important` }}
          defaultValue={workingDays}
          value={workingDays}
          onChange={handleWorkingDaysChange}
          MenuProps={{
            PaperProps: {
              sx: {
                zIndex: '1501 !important'
              }
            },
            sx: {
              zIndex: '1501 !important'
            }
          }}
          data-cy='user-profile-working-days-dropdwon'
        >
          {WORK_DAYS.map(role => {
            return (
              <MenuItem
                key={role}
                value={role}
                sx={{ width: '35rem !important' }}
              >
                {role}
              </MenuItem>
            );
          })}
        </DropDown>
        <Body1
          color={lightColorPalette.lbTextSubdued}
          paddingTop={2}
          paddingBottom={1}
        >
          Set your <strong>preferred time</strong> to receive updates:
        </Body1>
        <TimeDropdown
          selectedTime={selectedTime}
          setSelectedTime={setSelectedTime}
        />
        <Body1
          color={lightColorPalette.lbTextSubdued}
          paddingTop={2}
          paddingBottom={1}
        >
          Select your <strong>time zone</strong>:
        </Body1>
        <TimeZoneDropdown
          selectedTimeZone={timeZone}
          setSelectedTimeZone={setTimeZone}
        />
      </Stack>

      <Stack direction='row' justifyContent='space-between' paddingTop={3}>
        <LBButton
          variant='ghost'
          onClick={handleOnBack}
          data-cy='user-profile-add-work-hours-back-btn'
        >
          Back
        </LBButton>
        <LBButton
          disabled={!timeZone || !selectedTime}
          onClick={handleCompleteProfile}
          data-cy='user-profile-add-work-hours-complete-btn'
        >
          Complete Profile
        </LBButton>
      </Stack>
    </>
  );
}

export default AddWorkHoursStep;
